import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AuthState } from '@citydeals/auth';
import { isNil } from '@citydeals/common';
import { environment } from '@citydeals/env';
import { CheckoutEvent, ThankYouEvent, TrackService, ThankYouEnhancedConversionsEvent, PaymentTiktokEvent, TiktokTrackService, EventProductTiktok, OpenAiTrackService } from '@citydeals/track';
import { Store } from '@ngxs/store';
import { BasketState } from '..';
import { BasketPaymentPayload } from './model/basket-payment-payload.model';
import { PackageGiftPdfModel } from './model/voucher-design.model';
import { BehaviorSubject, Observable } from 'rxjs';


@Injectable({
  providedIn: 'root',
})
export class BasketService {
  private basketFormValidSubject = new BehaviorSubject<boolean>(false);
  basketFormValid$ = this.basketFormValidSubject.asObservable();

  constructor(
    private store: Store,
    private httpClient: HttpClient,
    private trackService: TrackService,
    private tiktokTrackService: TiktokTrackService,
    private openAiTrackService: OpenAiTrackService,
  ) {}

  setBasketFormValid(isValid: boolean) {
    this.basketFormValidSubject.next(isValid);
  }

  paymentInit(payload: BasketPaymentPayload) {
    return this.httpClient.post<{ data: { redirect: string } }>(
      `${environment.backendUrl}/payment/init`,
      payload
    );
  }

  trackSteps(step: number) {
    const offers = this.store.selectSnapshot(BasketState.offers);
    const currentUser = this.store.selectSnapshot(AuthState.currentUser);
    const products = offers.map((offer) => ({
      name: offer.voucherTitle,
      id: offer.id,
      price: offer.finalPrice,
      quantity: offer.quantity,
    }));

    const event = new CheckoutEvent(currentUser?.userId, step, products);
    this.trackService.track(event);
    // TIKTOK track AddPaymentInfo if step number is 3 (cart page 3. step)
    if (step == 3) {
        this.trackTiktokAddPaymentInfo();
    }
  }

  trackPayment(payRef: string) {
    const offers = this.store.selectSnapshot(BasketState.offers);
    const currentUser = this.store.selectSnapshot(AuthState.currentUser);
    const currentAddress= this.store.selectSnapshot(BasketState.addressForm);
    const customerEmail = !isNil(currentUser) ? currentUser.email : currentAddress?.email || '';

    const revenue = offers.reduce((prev, curr) => {
      prev += curr.finalPrice;
      return prev;
    }, 0);
    const products = offers.map((offer) => ({
      name: offer.voucherTitle,
      id: offer.id,
      price: offer.finalPrice,
      quantity: offer.quantity,
    }));

    const event = new ThankYouEvent(
      currentUser?.userId,
      customerEmail,
      payRef,
      revenue,
      products
    );
    this.trackService.track(event);
  }

  trackPaymentEnhancedConversion() {
    const currentUser = this.store.selectSnapshot(AuthState.currentUser);
    const basketPageUserData = this.store.selectSnapshot(BasketState.addressForm);
    const customerEmail = !isNil(currentUser) ? currentUser.email : basketPageUserData?.email || '';
    const customerPhoneNumber = !isNil(currentUser) ? '':'';
    const customerFirstName = !isNil(currentUser) ? currentUser.firstName : basketPageUserData?.firstName || '';
    const customerLastName = !isNil(currentUser) ? currentUser.lastName : basketPageUserData?.lastName || '';
    const customerStreet = !isNil(currentUser) ? currentUser.addressName : basketPageUserData?.addressName || '';
    const customerCity = !isNil(currentUser) ? currentUser.settlement : basketPageUserData?.settlement || '';
    const customerRegion = '';
    const customerPostalCode = !isNil(currentUser) ? currentUser.zipcode : basketPageUserData?.zipcode || '';
    const customerCountry = '';

    const event = new ThankYouEnhancedConversionsEvent (
        currentUser?.userId,
        customerEmail,
        customerPhoneNumber,
        customerFirstName,
        customerLastName,
        customerStreet,
        customerCity,
        customerRegion,
        customerPostalCode,
        customerCountry
      );
      if (currentUser) {
        this.trackService.track(event);
      }
  }

  trackTiktokPayment(payRef?: string) {
    const offers = this.store.selectSnapshot(BasketState.offers);
    // const currentUser = this.store.selectSnapshot(AuthState.currentUser);
    // const currentAddress= this.store.selectSnapshot(BasketState.addressForm);
    // const customerEmail = !isNil(currentUser) ? currentUser.email : currentAddress?.email || '';

    let tiktokProducts: EventProductTiktok[] = offers.map((offer) => ({
        id: offer.id,
        name: offer.voucherTitle,
        price: offer.finalPrice,
        type: 'product',
        currency: 'HUF',
    }));

    const event = new PaymentTiktokEvent('CompletePayment', tiktokProducts);
    this.tiktokTrackService.track(event);
  }

  /**
   * OpenAI (ChatGPT Ads) `order_created` esemény (CITYDEALS-719).
   * @param transactionId A rendelés transaction_id-ja – a backend is ezzel küldi (deduplikáció).
   */
  trackOpenAiPayment(transactionId: string | null | undefined) {
    const offers = this.store.selectSnapshot(BasketState.offers);
    // a végösszeg ugyanúgy, mint a trackPayment revenue-nál
    const amount = offers.reduce((sum, offer) => sum + offer.finalPrice, 0);
    this.openAiTrackService.trackOrderCreated(
      transactionId,
      amount,
      offers.map(offer => ({ id: offer.id, name: offer.voucherTitle, quantity: offer.quantity }))
    );
  }

  trackTiktokAddPaymentInfo(payRef?: string) {

    const offers = this.store.selectSnapshot(BasketState.offers);

    let tiktokProducts: EventProductTiktok[] = offers.map((offer) => ({
        id: offer.id,
        name: offer.voucherTitle,
        price: offer.finalPrice,
        type: 'product',
        currency: 'HUF',
    }));

    const event = new PaymentTiktokEvent('AddPaymentInfo', tiktokProducts);
    this.tiktokTrackService.track(event);
  }

  getGiftPdf(payload: PackageGiftPdfModel): Observable<Blob> {
    return this.httpClient.post(
      `${environment.backendUrl}/client/pdfdownload/sample`,
      payload,
      { responseType: 'blob' } // itt nincs generikus típus
    );
  }
}
